import { get, post } from '@dc/ui-library/src/helpers/network';
import { resolveByCountry } from '@dc/ui-library/src/helpers/environment';
import { getQueryParameter } from '@dc/ui-library/src/helpers/urlManipulation';
import { satelliteTrack } from '@dc/data-layer/src/utils.js';
import {
  setRenderReactProductList, setPendingProductsRequest,
  setPendingLocationRequest, setProductsPostXhrError, setLocationSearchEnabled
} from './listingPageSetup';
import { setHeaderBanner } from './listingPageBanners';
import {
  setPaginationPageList, setPaginationResultsMessage,
  setCategoryProductCount
} from './listingPagePagination';
import {
  setEmailMeBackXhrResponse, storeListingXhrResponse,
  setActiveStoredResponse
}
  from './listingPageProducts';
import createAction from './createAction';
import {
  getActiveStoredResponse,
  findStoredValidResponse, getCurrentPaginatorPage, scrollToResultsMessage
} from '../../components/react/ListingPage/helpers';
import { restorePreviousFiltersSetup, setFiltersSetup } from './listingPageFilters';
import {
  buildListingPageXhrPostBody, prepareFiltersPayload,
  setDataLayerRefinement, pushBrowserHistoryCurrentFilters,
  validateFilters
} from '../../components/react/ListingPage/components/Filtering/filterHelpers';
import initPageTitle from '../../components/react/ListingPage/initPageTitle';
import * as UISettings from '../../utils/UISettings';

import { promiseWithTimeout } from '../../utils';
import initBanners from '../../components/react/ListingPage/initBanners';

const validStatuses = Array.from(Array(100).keys()).map((i) => i + 200); // [200, 201, ..., 299]
const xhrOptions = {
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
};

// 1. Actions
const XHR_ALREADY_IN_PROGRESS = 'network/XHR_ALREADY_IN_PROGRESS';

// 2. Action creators
function xhrAlreadyInProgress() {
  return createAction(XHR_ALREADY_IN_PROGRESS)();
}

// 3. Variables
const domain = window.location.origin;

export function fetchListingPageData(whatChanged, updateBrowserHistory = true) {
  return (dispatch, getState) => {
    const state = getState();

    validateFilters();

    // If there was some error in previous XHR call, remove it before creating new XHR call
    if (state.listingPage.pageSetup.xhrStatus.productsPostXhrError !== null) {
      dispatch(setProductsPostXhrError(null));
    }

    if (state.listingPage.pageSetup.xhrStatus.pendingProductsRequest === true) {
      window.appState.dispatch(xhrAlreadyInProgress());
      return Promise.reject(new Error('Listing page data XHR already in progress.'));
    }

    // Remove location search spinning animation before loading new products.
    if (state.listingPage.pageSetup.xhrStatus.pendingLocationRequest === true) {
      window.appState.dispatch(setPendingLocationRequest(false));
    }

    // Cache handling: Decide if it's possible to use cached data in storedResponses array
    // or needed to fetch new data from server.
    const storedResponse = findStoredValidResponse({
      postBody: buildListingPageXhrPostBody()
    });

    if (storedResponse) {
      // Use cached data, still relevant according to cacheTimeout, no need to send new XHR.
      dispatch(setActiveStoredResponse(storedResponse.uuid));
      dispatch(setPaginationPageList(storedResponse.data.pagination.pageList));
      dispatch(setPaginationResultsMessage(storedResponse.data.pagination.showingResultsLabel));
      dispatch(setFiltersSetup(prepareFiltersPayload(storedResponse.data.filter)));
      dispatch(setHeaderBanner(storedResponse.data.banner));
      dispatch(setLocationSearchEnabled(
        Boolean(storedResponse.stockChecker && storedResponse.stockChecker.isLocationSearchEnabled)
      ));

      if (shouldFetchProductsAvailabilities(getState())) {
        window.dispatchEvent(new CustomEvent('loadAjaxProductsAvailabilities'));
      }

      if (updateBrowserHistory) {
        pushBrowserHistoryCurrentFilters();
      }

      scrollToResultsMessage();
      initPageTitle();
      initBanners();

      return Promise.resolve();
    }

    let xhrPromise;
    if (whatChanged === 'pagination') {
      xhrPromise = getListingPageDataPaginated(dispatch);
    } else {
      xhrPromise = getListingPageDataFilters(dispatch);
    }

    return xhrPromise.then(() => {
      if (updateBrowserHistory) {
        pushBrowserHistoryCurrentFilters();
      }

      scrollToResultsMessage();
      initPageTitle();
      initBanners();
    }).catch(() => {
      scrollToResultsMessage();
    });
  };
}

export function getListingPageSortBy(dispatch, sortType) {
  const sortUrls = UISettings.get('component.sortUrls');
  const url = sortUrls[(Object.keys(sortUrls).filter((key) => key === sortType))[0]];
  window.location.href = `${domain}${url}`;

  dispatch(setPendingProductsRequest(true));

  if (window.Search_VisitorSegmentId) {
    xhrOptions.headers['X-Visitor-Segment-Id'] = window.Search_VisitorSegmentId;
  }

  return promiseWithTimeout(
    get(`${domain}${url}`, validStatuses, xhrOptions), getXhrTimeout()
  ).then((payload) => {
    setDataLayerRefinement();

    dispatch(storeListingXhrResponse(
      { ...payload, request: { postBody: buildListingPageXhrPostBody() } }
    ));
    dispatch(setPendingProductsRequest(false));
  }, (e) => {
    window.appState.dispatch(
      {
        type: 'EVENTS_FAILURE_SORT_BY_RESPONSE',
        payload: {
          errorType: e.message,
          interactionType: 'sortBy change'
        }
      }
    );
    satelliteTrack('refinementUpdate');
    throw e;
  }).catch((e) => {
    dispatch(setPendingProductsRequest(false));
    window.location.href = `${domain}${url}`;
    throw e;
  });
}

export function getListingPageDataFilters(dispatch) {
  const url = `${domain}/api/shopping/product-listing/page`;
  dispatch(setPendingProductsRequest(true));
  const xhrPostBody = buildListingPageXhrPostBody();
  xhrPostBody.pageNumber = 1;

  let xhrPromise;
  if (window.Search_VisitorSegmentId) {
    xhrOptions.headers['X-Visitor-Segment-Id'] = window.Search_VisitorSegmentId;
  }

  const getURLBase64Params = `${url}/json/${btoa(JSON.stringify(xhrPostBody))}`;
  if (getURLBase64Params.length < 2000) {
    xhrPromise = promiseWithTimeout(
      get(getURLBase64Params, validStatuses, xhrOptions), getXhrTimeout()
    );
  } else {
    xhrPromise = promiseWithTimeout(
      post(url, xhrPostBody, /* useJSON = */ true), getXhrTimeout()
    );
  }

  // POST is always requesting page 1. Do not change it directly in buildListingPageXhrPostBody,
  // it may break cache handling. Needs better/proper refactor, this is just quick dirty solution
  // before release

  return xhrPromise.then((payload) => {
    setDataLayerRefinement();

    dispatch(storeListingXhrResponse({ ...payload, request: { postBody: xhrPostBody } }));
    dispatch(setPaginationPageList(payload.pagination.pageList));
    dispatch(setPaginationResultsMessage(payload.pagination.showingResultsLabel));
    dispatch(setCategoryProductCount(payload.pagination.itemsNumberPerPage.categoryProductCount));
    dispatch(setPendingProductsRequest(false));
    dispatch(setRenderReactProductList());
    dispatch(setFiltersSetup(prepareFiltersPayload(payload.filter)));
    dispatch(setHeaderBanner(payload.banner));
    dispatch(setLocationSearchEnabled(
      Boolean(payload.stockChecker && payload.stockChecker.isLocationSearchEnabled)
    ));

    // Dispatch CustomEvent to old postcode checker to fetch availabilities for currently
    // rendered products
    if (payload.productList.length > 0) {
      window.dispatchEvent(new CustomEvent('loadAjaxProductsAvailabilities'));
    }

  }, (e) => {
    dispatch(setProductsPostXhrError(e.message));
    window.appState.dispatch(
      {
        type: 'EVENTS_FAILURE_AJAX_FILTERS_RESPONSE',
        payload: {
          errorType: e.message,
          interactionType: 'filter update'
        }
      }
    );
    satelliteTrack('refinementUpdate');
    throw e;
  }).catch((e) => {
    dispatch(setPendingProductsRequest(false));
    dispatch(restorePreviousFiltersSetup());
    throw e;
  });
}

export function getListingPageDataPaginated(dispatch) {
  const paginatorPage = getCurrentPaginatorPage();
  if (!paginatorPage) {
    return Promise.reject(new Error(
      `Unable to load paginator page data, therefore unable to 
        retrieve URL to send XHR to or to set new browserhistory state`
    ));
  }

  dispatch(setPendingProductsRequest(true));

  if (window.Search_VisitorSegmentId) {
    xhrOptions.headers['X-Visitor-Segment-Id'] = window.Search_VisitorSegmentId;
  }

  return promiseWithTimeout(
    get(paginatorPage.dataUrl, validStatuses, xhrOptions), getXhrTimeout()
  ).then((payload) => {
    setDataLayerRefinement();

    dispatch(storeListingXhrResponse(
      { ...payload, request: { postBody: buildListingPageXhrPostBody() } }
    ));
    dispatch(setPaginationPageList(payload.pagination.pageList));
    dispatch(setPaginationResultsMessage(payload.pagination.showingResultsLabel));
    dispatch(setCategoryProductCount(payload.pagination.itemsNumberPerPage.categoryProductCount));
    dispatch(setPendingProductsRequest(false));
    dispatch(setRenderReactProductList());
    dispatch(setFiltersSetup(prepareFiltersPayload(payload.filter)));
    dispatch(setHeaderBanner(payload.banner));
    dispatch(setLocationSearchEnabled(
      Boolean(payload.stockChecker && payload.stockChecker.isLocationSearchEnabled)
    ));

    // Dispatch CustomEvent to old postcode checker to fetch availabilities for currently
    // rendered products
    if (payload.productList.length > 0) {
      window.dispatchEvent(new CustomEvent('loadAjaxProductsAvailabilities'));
    }
  }, (e) => {
    window.appState.dispatch(
      {
        type: 'EVENTS_FAILURE_AJAX_FILTERS_RESPONSE',
        payload: {
          errorType: e.message,
          interactionType: 'pagination page change'
        }
      }
    );
    satelliteTrack('refinementUpdate');
    throw e;
  }).catch((e) => {
    dispatch(setPendingProductsRequest(false));
    window.location.href = paginatorPage.url;
    throw e;
  });
}

export function postEmailMeBackForm(params) {
  return (dispatch) => {
    const xhrBody = Object.entries({
      sFUPID: params.productID,
      action: 'subscribeOOS',
      title_id: params.titleId,
      first_name: params.name,
      last_name: params.surname,
      email: params.email
    }).map((val) => `${val[0]}=${val[1]}`).join('&');

    const url = `${resolveByCountry('/gb/uk/', '/ie/uk/')}out_of_stock/ajax.html`;

    fetch(url, {
      method: 'post',
      async: true,
      crossDomain: true,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: xhrBody
    }).then((data) => data.json()).then((response) => {
      if (response.result === 'OK') {
        dispatch(setEmailMeBackXhrResponse(response.content));
      }
    }).catch(() => {
      dispatch(setEmailMeBackXhrResponse(null));
    });
  };
}

function shouldFetchProductsAvailabilities(state) {
  const currentStoredResponse = getActiveStoredResponse();
  // If location search component has set location AND current product list does not have loaded
  // that location data -> it's needed to fetch location data for current product list.
  // Location data has to be also fetched
  // when the current location is different from location in stored response.
  return !!(state.listingPage.locationSearch.currentFoundLocation &&
    state.listingPage.locationSearch.currentFoundLocation.location &&
    (currentStoredResponse.data.placeSpecificAvailabilityPostcode !==
      state.listingPage.locationSearch.currentFoundLocation.postcode) &&
    currentStoredResponse.data.productList.length > 0);
}

export function getCacheTimeout() {
  const defaultCacheTimeout = 300 * 1000; // 5 minutes
  return window.devListingPageCacheTimeout ||
    parseInt(getQueryParameter(window.location, 'devListingPageCacheTimeout', ''), 10) ||
    defaultCacheTimeout;
}

export function getXhrTimeout() {
  const defaultXhrTimeout = 10000; // 10 seconds
  return window.devListingPageXhrTimeout ||
    parseInt(getQueryParameter(window.location, 'devListingPageXhrTimeout', ''), 10) ||
    defaultXhrTimeout;
}

// PSA fetch real data
export function getRequestForBrainDataForPSA(mainProduct) {
  const defaultValidStatuses = Array.from(Array(100).keys()).map((i) => i + 200); // [200, 201, ..., 299]
  const storeURL = UISettings.get('component.psa.api');
  const addr = storeURL.split('/', 3);
  addr[1] = '//';
  const hostStoreURL = addr.join('');
  // eslint-disable-next-line max-len
  return get(`${hostStoreURL}/store/api/products/${mainProduct}/popup_style_attach/visitors/0`, defaultValidStatuses);
}

export function fetchUserEligibilityCheckData(eligibilityCheckId, productId) {
  let fetchData = {};

  if (eligibilityCheckId) {
    fetchData = { eligibilityCheckId: eligibilityCheckId.toString() };
  }

  if (productId) {
    fetchData = { ...fetchData, productId: productId.toString() };
  }

  const eligibilityCheckAddressKey = UISettings.get('component.eligibilityCheck.api');
  const uecApiUrl = eligibilityCheckAddressKey
    ? `${domain}${eligibilityCheckAddressKey}`
    : `${domain}/api/shopping/tariff/eligibility-result`;

  return promiseWithTimeout(
    new Promise((resolve, reject) => setTimeout(() => {
      resolve(post(uecApiUrl, fetchData));
    }, 0)), 12000
  );
}

export function fetchUpgradeEligibilityCheckId() {
  const upgradeEligibilityIdEndpointUrl =
  `${window.location.origin}/api/shopping/tariff/eligibility-check/id`;
  return get(upgradeEligibilityIdEndpointUrl, validStatuses);
}

export function fetchUserEligibilityCheckStatus() {
  const payloadData = { productId: parseInt(window.digitalData.product[0].productID, 10) };
  return post(`${domain}/api/shopping/product-detail/checkout-block`, payloadData);
}
